import type { CatalogEvent } from "@livcg/schema";
import { formatEventMinute } from "@livcg/schema";
import { useState } from "react";
import { sendRaw } from "../shared/sendRaw.js";
import type { ConfirmDialogProps } from "../ui/index.js";
import { cancelSteps } from "./airSteps.js";
import { deleteImpactLine, eventLabel } from "./eventBadges.js";
import type { useMatchDetail } from "./useMatchDetail.js";
import { yellowCancelBanner } from "./yellowCancel.js";

type MatchDetail = ReturnType<typeof useMatchDetail>;

/** Deleting a log row — with a confirmation dialog. EventLog (Control
 *  Room) and TabLog (Classic) share the same flow: pressing Trash does NOT
 *  PERFORM the action, it only opens the dialog. The dialog names the event in full
 *  (minute · type · team · player) — if the wrong row is pressed by accident
 *  the operator sees it before deleting. */
export function useEventDelete(slug: string, md: MatchDetail) {
  const [pending, setPending] = useState<CatalogEvent | null>(null);
  const [announce, setAnnounce] = useState(true);

  const banner = pending ? yellowCancelBanner(pending, md.detail, md.coach) : null;

  const confirm = () => {
    const id = pending?.id;
    setPending(null);
    if (!id) return;
    void sendRaw("event.delete", { slug, eventId: id }).then(() => md.refetch());
    if (announce && banner) {
      void sendRaw("overlay.play", {
        slug,
        key: null,
        steps: cancelSteps(banner.mode, banner.data),
      });
    }
  };

  const lines: string[] = pending
    ? [
        `${formatEventMinute(pending.minute, pending.second, pending.data?.added)}ʼ · ${eventLabel(pending.type)} · ${md.teamShort(pending.teamId)} · ${md.playerName(pending.playerId)}`,
        deleteImpactLine(pending.type),
      ].filter((line): line is string => line !== null)
    : [];

  const confirmProps: ConfirmDialogProps = {
    open: pending !== null,
    title: "Voqeani o'chirish",
    description: lines.join("\n\n"),
    confirmLabel: "O'chirish",
    tone: "danger",
    onConfirm: confirm,
    onCancel: () => setPending(null),
    children: banner ? (
      <label className="mt-3 flex items-center gap-2 text-[13px] text-ink">
        <input type="checkbox" checked={announce} onChange={(e) => setAnnounce(e.target.checked)} />
        Efirda «Sariq bekor qilindi» ko'rsatish
      </label>
    ) : undefined,
  };

  const requestDelete = (event: CatalogEvent) => {
    setAnnounce(true);
    setPending(event);
  };

  return { requestDelete, confirmProps };
}
